import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddCategoryModal from '../components/AddCategoryModal';
import EditCategoryModal from '../components/EditCategoryModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import './CategoriesPage.css';

// Importa la configurazione API
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/shop/backend/api' 
  : 'http://efwypvu.cluster021.hosting.ovh.net/shop/backend/api';

const CategoriesPage = () => {
  const navigate = useNavigate();
  const publicUrl = process.env.PUBLIC_URL || '';
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false); // State for edit modal
  const [editingCategory, setEditingCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false); // State for delete confirmation modal
  const [categoryToDelete, setCategoryToDelete] = useState(null); // Category to be deleted
  const [deleteMessage, setDeleteMessage] = useState('');

  // Filtri e ordinamento
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filters, setFilters] = useState({
    searchText: '',
    sortBy: 'nome_categoria', // 'nome_categoria', 'data_creazione'
    sortOrder: 'asc' // 'asc', 'desc'
  });

  useEffect(() => {
    checkAuth();
    loadCategories();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [categories, filters]);

  const checkAuth = () => {
    console.log('🔍 Verifica autenticazione locale...');
    
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    const adminData = localStorage.getItem('admin_data');
    
    if (!isLoggedIn || !adminData) {
      console.log('❌ Non autenticato, reindirizzamento al login...');
      navigate('/amministrazione');
      return;
    }
    
    console.log('✅ Autenticazione verificata!');
    setLoading(false);
  };

  const loadCategories = async () => {
    try {
      console.log('📁 Caricamento categorie...');
      
      const response = await fetch(`${API_BASE_URL}/categorie.php`);
      const data = await response.json();
      
      console.log('📋 Categorie caricate:', data);
      
      if (data.success) {
        setCategories(data.data || []);
      } else {
        console.error('❌ Errore caricamento categorie:', data.error);
      }
    } catch (err) {
      console.error('💥 Errore caricamento categorie:', err);
    }
  };

  const handleAddCategory = async (categoryData) => {
    console.log('📁 Aggiunta categoria:', categoryData);
    
    try {
      const response = await fetch(`${API_BASE_URL}/categorie.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(categoryData),
      });

      const data = await response.json();
      console.log('📋 Risposta aggiunta categoria:', data);

      if (!data.success) {
        throw new Error(data.error || 'Errore durante l\'aggiunta della categoria');
      }

      console.log('✅ Categoria aggiunta con successo!');
      loadCategories(); // Ricarica la lista
      
    } catch (err) {
      console.error('💥 Errore aggiunta categoria:', err);
      throw err;
    }
  };

  const handleEditCategory = async (categoryId, categoryData) => {
    console.log('✏️ Modifica categoria:', categoryId, categoryData);
    
    try {
      const response = await fetch(`${API_BASE_URL}/categorie.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: categoryId,
          ...categoryData
        }),
      });

      const data = await response.json();
      console.log('📋 Risposta modifica categoria:', data);

      if (!data.success) {
        throw new Error(data.error || 'Errore durante la modifica della categoria');
      }

      console.log('✅ Categoria modificata con successo!');
      loadCategories(); // Ricarica la lista
      
    } catch (err) {
      console.error('💥 Errore modifica categoria:', err);
      throw err;
    }
  };

  const handleDeleteCategory = (category) => {
    const isDefault = Number(category.is_default) === 1;
    if (isDefault) {
      alert('La categoria predefinita "Senza Categoria" non può essere eliminata.');
      return;
    }

    const productCount = category.numero_prodotti || 0;
    const message = productCount > 0
      ? `Eliminando "${category.nome}" ${productCount === 1 ? 'il prodotto associato verrà' : `${productCount} prodotti associati verranno`} spostati automaticamente nella categoria "Senza Categoria". Confermi l'operazione?`
      : `Sei sicuro di voler eliminare la categoria "${category.nome}"?`;

    setDeleteMessage(message);
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/categorie.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: categoryToDelete.id_categoria }),
      });

      const data = await response.json();
      console.log('📋 Risposta eliminazione categoria:', data);

      if (!data.success) {
        throw new Error(data.error || 'Errore durante l\'eliminazione della categoria');
      }

      console.log('✅ Categoria eliminata con successo!');
      loadCategories(); // Ricarica la lista
      setShowDeleteModal(false);
      setCategoryToDelete(null);
      setDeleteMessage('');
      
    } catch (err) {
      console.error('💥 Errore eliminazione categoria:', err);
      alert('Errore durante l\'eliminazione: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Funzioni per i filtri
  const applyFilters = () => {
    let filtered = [...categories];

    // Filtro per testo di ricerca
    if (filters.searchText.trim()) {
      const searchTerm = filters.searchText.toLowerCase();
      filtered = filtered.filter(category => {
        const categoryName = category.nome?.toLowerCase() || '';
        return categoryName.includes(searchTerm);
      });
    }

    // Ordinamento
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (filters.sortBy) {
        case 'nome_categoria':
          aValue = a.nome?.toLowerCase() || '';
          bValue = b.nome?.toLowerCase() || '';
          break;
        case 'data_creazione':
          aValue = new Date(a.data_creazione) || new Date(0);
          bValue = new Date(b.data_creazione) || new Date(0);
          break;
        default:
          return 0;
      }

      if (filters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredCategories(filtered);
  };

  const handleSearchChange = (e) => {
    setFilters(prev => ({
      ...prev,
      searchText: e.target.value
    }));
  };

  const handleSortChange = (e) => {
    const [sortBy, sortOrder] = e.target.value.split('-');
    setFilters(prev => ({
      ...prev,
      sortBy: sortBy,
      sortOrder: sortOrder
    }));
  };

  const resetFilters = () => {
    setFilters({
      searchText: '',
      sortBy: 'nome_categoria',
      sortOrder: 'asc'
    });
  };

  if (loading) {
    return (
      <div className="categories-page">
        <div className="loading">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="categories-page admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <img src={`${publicUrl}/logomuseo.png`} alt="Museo Stibbert" className="sidebar-logo" />
 
        </div>

        <div className="sidebar-admin-title">
          <h1><strong style={{color: 'var(--bianco), font-weight: 700, font-size: 1.2rem'}}>Museo Stibbert</strong> Amministrazione</h1>
        </div>

        <nav className="sidebar-nav">
          <button className="nav-item" onClick={() => navigate('/amministrazione/dashboard')}>
            <i className="fas fa-tachometer-alt"></i>
            <span>Dashboard</span>
          </button>
          <button className="nav-item active">
            <i className="fas fa-tags"></i>
            <span>Categorie</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/prodotti')}>
            <i className="fas fa-box"></i>
            <span>Prodotti</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/ordini')}>
            <i className="fas fa-shopping-cart"></i>
            <span>Ordini</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/impostazioni')}>
            <i className="fas fa-cog"></i>
            <span>Impostazioni</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item logout-item" onClick={() => {
            localStorage.removeItem('admin_logged_in');
            localStorage.removeItem('admin_data');
            navigate('/amministrazione');
          }}>
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <div className="page-header">
          <div className="header-content">
            <h1>Categorie</h1>
            <div className="header-actions">
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddModal(true)}
              >
                <i className="fas fa-plus"></i>
                Aggiungi Categoria
              </button>
            </div>
          </div>
        </div>

        <div className="main-content">
          {/* Filtri */}
          <div className="filters-bar">
            {/* Ricerca per testo - Sinistra */}
            <div className="search-group">
              <input
                id="search-input"
                type="text"
                placeholder="Cerca per nome categoria..."
                value={filters.searchText}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>

            {/* Ordinamento e Reset - Destra */}
            <div className="controls-group">
              <div className="sort-group">
                <label htmlFor="sort-select">Ordina per:</label>
                <select
                  id="sort-select"
                  className="sort-select"
                  value={`${filters.sortBy}-${filters.sortOrder}`}
                  onChange={handleSortChange}
                >
                  <option value="nome_categoria-asc">Nome (A-Z)</option>
                  <option value="nome_categoria-desc">Nome (Z-A)</option>
                  <option value="data_creazione-desc">Data (Più Recenti)</option>
                  <option value="data_creazione-asc">Data (Più Vecchi)</option>
                </select>
              </div>
              <button className="reset-filters-btn" onClick={resetFilters}>
                Reset Filtri
              </button>
            </div>
          </div>

          <div className="categories-table-container">
            {filteredCategories.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-folder-open"></i>
                <h3>Nessuna categoria presente</h3>
                <p>Clicca su "Aggiungi Categoria" per creare la prima categoria</p>
              </div>
            ) : (
              <table className="categories-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Prodotti Associati</th>
                    <th>Data Creazione</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.map((category) => {
                    const isDefault = Number(category.is_default) === 1;
                    return (
                    <tr key={category.id_categoria}>
                      <td className="category-name">
                          <div className="category-name-wrapper">
                        <strong>{category.nome}</strong>
                            {isDefault && (
                              <span className="default-badge">
                                Default
                              </span>
                            )}
                          </div>
                      </td>
                      <td className="products-count">
                        <span className="count-badge">
                          {category.numero_prodotti || 0}
                        </span>
                      </td>
                      <td className="category-date">
                        {formatDate(category.data_creazione)}
                      </td>
                      <td className="category-actions">
                          {isDefault ? (
                            <span className="actions-disabled">
                              Non modificabile
                            </span>
                          ) : (
                            <>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            setEditingCategory(category);
                            setShowEditModal(true);
                          }}
                          title="Modifica"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteCategory(category)}
                          title="Elimina"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                            </>
                          )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {/* Modal Aggiungi Categoria */}
      <AddCategoryModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddCategory={handleAddCategory}
      />

      {/* Modal Modifica Categoria */}
      <EditCategoryModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingCategory(null);
        }}
        onEditCategory={(data) => {
          handleEditCategory(editingCategory.id_categoria, data);
          setShowEditModal(false);
          setEditingCategory(null);
        }}
        initialData={editingCategory}
      />

      {/* Modal Conferma Eliminazione */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCategoryToDelete(null);
          setDeleteMessage('');
        }}
        onConfirm={confirmDelete}
        message={deleteMessage || (categoryToDelete ? `Sei sicuro di voler eliminare la categoria "${categoryToDelete.nome}"?` : "Sei sicuro di voler eliminare questa categoria?")}
      />
    </div>
  );
};

export default CategoriesPage;
